/**
 * /notifications (OCP-597) - the operator inbox: every Web Push the control
 * plane sent, persisted with category, severity and the tenant / server /
 * account it concerns. Newest first, filterable, mark-read per row or all
 * at once, and a link to the target's admin page (the label survives the
 * target's deletion, so a row about a deleted tenant still names it).
 *
 * OCP-669 - autonomous triage: a warning / critical row shows its alert
 * ticket and triage state (open = the always-on agent is on it and the
 * device push is deferred, acked, resolved, escalated) with the operator's
 * own ack / resolve / escalate actions; filter by triage state.
 */
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { api, ApiError } from '../lib/api';
import { fmtDateTime } from '../lib/format-time';
import { ActorBadge } from './AdminAuditPage';

export type NotificationCategory =
  | 'ai_billing' | 'ai_gateway' | 'fleet' | 'maintenance' | 'billing' | 'signup'
  | 'seats' | 'host' | 'tenant' | 'referrals' | 'security' | 'system' | 'support';
export type NotificationSeverity = 'info' | 'warning' | 'critical';
export type TriageState = 'none' | 'open' | 'acked' | 'resolved' | 'escalated';

export interface OperatorNotification {
  id: string;
  category: NotificationCategory;
  severity: NotificationSeverity;
  title: string;
  body: string;
  url: string;
  target: { type: 'tenant' | 'server' | 'account'; id: string; label: string | null } | null;
  dedupe_key: string | null;
  delivered_count: number;
  triage_state: TriageState;
  ticket_key: string | null;
  /** OCP-712 - distinct conditions on this row's ticket (> 1 = one incident, several targets) and how often this key fired. */
  incident_size?: number;
  occurrence?: number;
  ticket_id: string | null;
  agent_notified_at: string | null;
  device_push_deferred: boolean;
  acked_at: string | null;
  acked_by: string | null;
  resolved_at: string | null;
  resolved_by: string | null;
  resolution: string | null;
  escalated_at: string | null;
  escalated_by: string | null;
  escalation_reason: string | null;
  triage_error: string | null;
  ack_pushed_at?: string | null;
  all_clear_pushed_at?: string | null;
  created_at: string;
  read_at: string | null;
  read_by: string | null;
}

const CATEGORIES: NotificationCategory[] = [
  'ai_billing', 'ai_gateway', 'fleet', 'maintenance', 'billing', 'signup', 'seats', 'host', 'tenant', 'referrals', 'security', 'system', 'support',
];
const PAGE = 50;
const TRIAGE_STATES: TriageState[] = ['open', 'acked', 'escalated', 'resolved', 'none'];

export function triageClasses(s: TriageState): string {
  if (s === 'escalated') return 'bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300';
  if (s === 'open') return 'bg-amber-50 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300';
  if (s === 'acked') return 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300';
  if (s === 'resolved') return 'bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300';
  return 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300';
}

export function severityClasses(s: NotificationSeverity): string {
  if (s === 'critical') return 'bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300';
  if (s === 'warning') return 'bg-amber-50 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300';
  return 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300';
}

export function targetHref(t: NonNullable<OperatorNotification['target']>): string {
  if (t.type === 'tenant') return `/tenants/${t.id}`;
  if (t.type === 'server') return `/servers/${t.id}`;
  return `/accounts/${t.id}`;
}

function fmt(iso: string): string {
  return fmtDateTime(iso);
}

export interface TriageActions {
  ack: (id: string) => void;
  resolve: (id: string) => void;
  escalate: (id: string) => void;
  /** OCP-669 - re-run a triage that never got its ticket (state none + triage_error). */
  retry: (id: string) => void;
}

const btn = 'rounded border border-gray-300 dark:border-gray-700 px-2 py-1 text-xs text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800';

/** OCP-699 - email -> kind for the acked / resolved / escalated by badges. */
export type ActorMap = Record<string, { kind: 'human' | 'bot'; display_name: string | null }>;

export function NotificationRow({ n, onRead, triage, actors = {} }: { n: OperatorNotification; onRead?: (id: string) => void; triage?: TriageActions; actors?: ActorMap }) {
  const triaged = n.triage_state !== 'none';
  const canAct = triage && triaged && n.triage_state !== 'resolved';
  const canRetry = triage && !triaged && n.severity !== 'info' && n.triage_error;
  return (
    <li
      data-testid="notification-row"
      className={`flex flex-wrap items-start justify-between gap-3 py-3 ${n.read_at ? 'opacity-70' : ''}`}
    >
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className={`rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase ${severityClasses(n.severity)}`}>{n.severity}</span>
          <span className="rounded bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 text-[10px] font-medium uppercase text-gray-600 dark:text-gray-300">{n.category}</span>
          <Link to={n.url} className="text-sm font-medium text-gray-900 dark:text-gray-100 hover:underline">
            {n.title}
          </Link>
          {!n.read_at && <span className="h-2 w-2 rounded-full bg-brand-500" aria-label="unread" />}
          {triaged && (
            <span data-testid="notification-triage" className={`rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase ${triageClasses(n.triage_state)}`}>
              {n.triage_state}{n.ticket_key ? ` ${n.ticket_key}` : ''}
            </span>
          )}
          {(n.incident_size ?? 1) > 1 && (
            <span data-testid="notification-incident" className="rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-gray-600 dark:bg-gray-800 dark:text-gray-300" title="Alerts of one kind inside the incident window share one ticket">
              incident, {n.incident_size} targets
            </span>
          )}
          {(n.occurrence ?? 1) > 1 && (
            <span data-testid="notification-occurrence" className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-amber-700 dark:bg-amber-900/40 dark:text-amber-300" title="How often this condition has raised an alert">
              occurrence {n.occurrence}
            </span>
          )}
        </div>
        <p className="mt-1 text-sm text-gray-700 dark:text-gray-300">{n.body}</p>
        <div className="mt-1 flex flex-wrap gap-x-3 text-xs text-gray-500">
          <span>{fmt(n.created_at)}</span>
          {n.target && (
            <Link to={targetHref(n.target)} className="text-brand-600 hover:underline" data-testid="notification-target">
              {n.target.type}: {n.target.label ?? n.target.id}
            </Link>
          )}
          <span>{n.delivered_count} device{n.delivered_count === 1 ? '' : 's'}</span>
          {n.read_at && <span>read by {n.read_by ?? '-'} {fmt(n.read_at)}</span>}
          {n.triage_state === 'open' && n.device_push_deferred && <span>push deferred, agent {n.agent_notified_at ? `woken ${fmt(n.agent_notified_at)}` : 'not woken'}</span>}
          {n.acked_at && <span>acked by {n.acked_by ?? '-'}<ActorBadge kind={actors[n.acked_by ?? '']?.kind} displayName={actors[n.acked_by ?? '']?.display_name} /> {fmt(n.acked_at)}</span>}
          {n.escalated_at && <span className="text-red-600 dark:text-red-400">escalated by {n.escalated_by ?? '-'}<ActorBadge kind={actors[n.escalated_by ?? '']?.kind} displayName={actors[n.escalated_by ?? '']?.display_name} /> {fmt(n.escalated_at)}: {n.escalation_reason ?? '-'}</span>}
          {n.resolved_at && <span className="text-green-700 dark:text-green-400">resolved by {n.resolved_by ?? '-'}<ActorBadge kind={actors[n.resolved_by ?? '']?.kind} displayName={actors[n.resolved_by ?? '']?.display_name} /> {fmt(n.resolved_at)}: {n.resolution ?? '-'}</span>}
          {n.ack_pushed_at && <span title="OCP-691: 'agent is on it' push">on-it push {fmt(n.ack_pushed_at)}</span>}
          {n.all_clear_pushed_at && <span className="text-green-700 dark:text-green-400" title="OCP-691: all-clear push">all-clear push {fmt(n.all_clear_pushed_at)}</span>}
          {n.triage_error && <span className="text-red-600 dark:text-red-400" data-testid="notification-triage-error">triage: {n.triage_error}</span>}
        </div>
      </div>
      {canRetry && (
        <button type="button" onClick={() => triage.retry(n.id)} data-testid="notification-retry-triage" className={btn}>Retry triage</button>
      )}
      {canAct && (
        <div className="flex flex-wrap gap-1">
          {n.triage_state === 'open' && (
            <button type="button" onClick={() => triage.ack(n.id)} data-testid="notification-ack" className={btn}>Ack</button>
          )}
          <button type="button" onClick={() => triage.resolve(n.id)} data-testid="notification-resolve" className={btn}>Resolve</button>
          {n.triage_state !== 'escalated' && (
            <button type="button" onClick={() => triage.escalate(n.id)} data-testid="notification-escalate" className={`${btn} text-red-700 dark:text-red-300`}>Escalate</button>
          )}
        </div>
      )}
      {!n.read_at && onRead && (
        <button
          type="button"
          onClick={() => onRead(n.id)}
          data-testid="notification-mark-read"
          className="rounded border border-gray-300 dark:border-gray-700 px-2 py-1 text-xs text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
        >
          Mark read
        </button>
      )}
    </li>
  );
}

export function AdminNotificationsPage() {
  const [items, setItems] = useState<OperatorNotification[]>([]);
  const [actors, setActors] = useState<ActorMap>({});
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [category, setCategory] = useState<'' | NotificationCategory>('');
  const [severity, setSeverity] = useState<'' | NotificationSeverity>('');
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [triageState, setTriageState] = useState<'' | TriageState>('');
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const params = new URLSearchParams();
      if (category) params.set('category', category);
      if (severity) params.set('severity', severity);
      if (unreadOnly) params.set('unread', 'true');
      if (triageState) params.set('triage_state', triageState);
      params.set('limit', String(PAGE));
      params.set('offset', String(offset));
      const r = await api<{ items: OperatorNotification[]; total: number; actors?: ActorMap }>(`/v1/admin/notifications?${params.toString()}`);
      setItems(r.items);
      setActors(r.actors ?? {});
      setTotal(r.total);
    } catch (err) {
      setError(err instanceof ApiError ? `Could not load notifications (HTTP ${err.status}).` : 'Could not load notifications.');
    }
  }, [category, severity, unreadOnly, triageState, offset]);

  useEffect(() => {
    void load();
  }, [load]);

  async function markRead(id: string) {
    try {
      await api(`/v1/admin/notifications/${id}/read`, { method: 'POST' });
      await load();
    } catch {
      setError('Could not mark the notification as read.');
    }
  }

  // OCP-669 - the operator's own triage verdicts (the agent uses the same endpoints through MCP).
  const triage: TriageActions = {
    ack: (id) => void triageCall(`${id}/ack`, undefined, 'acknowledge'),
    resolve: (id) => {
      const resolution = window.prompt('Resolution (what was checked, why it is not a problem):');
      if (resolution && resolution.trim().length >= 3) void triageCall(`${id}/resolve`, { resolution: resolution.trim() }, 'resolve');
    },
    escalate: (id) => {
      const reason = window.prompt('Escalation reason (pushes the alert to every operator device now):');
      if (reason && reason.trim().length >= 3) void triageCall(`${id}/escalate`, { reason: reason.trim() }, 'escalate');
    },
    retry: (id) => void triageCall(`${id}/triage`, undefined, 'retry the triage of'),
  };

  async function triageCall(path: string, body: Record<string, string> | undefined, verb: string) {
    try {
      await api(`/v1/admin/notifications/${path}`, { method: 'POST', body });
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? `Could not ${verb} the alert (HTTP ${err.status}).` : `Could not ${verb} the alert.`);
    }
  }

  async function markAll() {
    try {
      await api('/v1/admin/notifications/read-all', { method: 'POST' });
      await load();
    } catch {
      setError('Could not mark all as read.');
    }
  }

  return (
    <div className="mx-auto max-w-4xl">
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Notifications</h1>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
            Every alert the control plane pushed, with whom it concerns. Device push is delivered from
            Settings -&gt; App -&gt; Notifications; this inbox is the record. Warnings open an alert ticket for
            the always-on agent first and reach your devices only when escalated (or after the triage window).
          </p>
        </div>
        <button
          type="button"
          onClick={() => void markAll()}
          data-testid="notifications-read-all"
          className="rounded border border-gray-300 dark:border-gray-700 px-3 py-1.5 text-xs font-semibold text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
        >
          Mark all read
        </button>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-3 text-sm">
        <select value={category} onChange={(e) => { setOffset(0); setCategory(e.target.value as '' | NotificationCategory); }} data-testid="notifications-filter-category" className="rounded border border-gray-300 dark:border-gray-700 px-2 py-1 text-sm">
          <option value="">all categories</option>
          {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <select value={severity} onChange={(e) => { setOffset(0); setSeverity(e.target.value as '' | NotificationSeverity); }} className="rounded border border-gray-300 dark:border-gray-700 px-2 py-1 text-sm">
          <option value="">all severities</option>
          <option value="critical">critical</option>
          <option value="warning">warning</option>
          <option value="info">info</option>
        </select>
        <select value={triageState} onChange={(e) => { setOffset(0); setTriageState(e.target.value as '' | TriageState); }} data-testid="notifications-filter-triage" className="rounded border border-gray-300 dark:border-gray-700 px-2 py-1 bg-white dark:bg-gray-900">
          <option value="">any triage state</option>
          {TRIAGE_STATES.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <label className="flex items-center gap-1.5">
          <input type="checkbox" checked={unreadOnly} onChange={(e) => { setOffset(0); setUnreadOnly(e.target.checked); }} />
          <span>unread only</span>
        </label>
        <span className="text-xs text-gray-500">{total} total</span>
      </div>

      {error && (
        <div role="alert" className="mt-4 rounded border border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/30 px-3 py-2 text-sm text-red-700 dark:text-red-300">
          {error}
        </div>
      )}

      {items.length === 0 && !error && <p className="mt-6 text-sm text-gray-500">No notifications.</p>}
      <ul className="mt-4 divide-y divide-gray-100 dark:divide-gray-800" data-testid="notifications-list">
        {items.map((n) => <NotificationRow key={n.id} n={n} onRead={markRead} triage={triage} actors={actors} />)}
      </ul>

      {total > PAGE && (
        <div className="mt-4 flex items-center justify-between text-sm">
          <button type="button" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE))} className="rounded border border-gray-300 dark:border-gray-700 px-3 py-1 disabled:opacity-50">
            Newer
          </button>
          <span className="text-xs text-gray-500">{offset + 1}-{Math.min(offset + PAGE, total)} of {total}</span>
          <button type="button" disabled={offset + PAGE >= total} onClick={() => setOffset(offset + PAGE)} className="rounded border border-gray-300 dark:border-gray-700 px-3 py-1 disabled:opacity-50">
            Older
          </button>
        </div>
      )}
    </div>
  );
}
